// =============================================================================
// KpiCard — card compacta para un KPI numérico, opcionalmente navegable.
// Estilo consistente con Sidebar y Ficha 360°: slate + primary, Material Symbols.
// Sin spinners: si `loading`, muestra placeholder de pulso discreto.
// =============================================================================

import { useNavigate } from 'react-router-dom';

interface KpiCardProps {
  icon: string;              // Material Symbols nombre
  label: string;
  value: number | string;
  subLabel?: string;
  /** Destino al pulsar la card (drill-down). Si se omite, no es clicable. */
  to?: string;
  /** Tono del icono */
  tone?: 'primary' | 'amber' | 'red' | 'green' | 'slate' | 'violet';
  loading?: boolean;
}

const TONE_CLASSES: Record<NonNullable<KpiCardProps['tone']>, { bg: string; text: string }> = {
  primary: { bg: 'bg-primary/10', text: 'text-primary' },
  amber:   { bg: 'bg-amber-100',  text: 'text-amber-600' },
  red:     { bg: 'bg-red-100',    text: 'text-red-600' },
  green:   { bg: 'bg-green-100',  text: 'text-green-600' },
  slate:   { bg: 'bg-slate-100',  text: 'text-slate-600' },
  violet:  { bg: 'bg-violet-100', text: 'text-violet-600' },
};

export function KpiCard({
  icon,
  label,
  value,
  subLabel,
  to,
  tone = 'primary',
  loading = false,
}: KpiCardProps) {
  const navigate = useNavigate();
  const toneClasses = TONE_CLASSES[tone];

  const content = (
    <div className="flex items-start gap-3">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${toneClasses.bg}`}
      >
        <span className={`material-symbols-outlined text-xl ${toneClasses.text}`}>
          {icon}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        {loading ? (
          <>
            <div className="h-7 w-16 bg-slate-100 rounded animate-pulse mb-2" />
            <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
          </>
        ) : (
          <>
            <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
            <p className="text-xs text-slate-500 mt-1">{label}</p>
            {subLabel && (
              <p className="text-[11px] text-slate-400 mt-0.5">{subLabel}</p>
            )}
          </>
        )}
      </div>
    </div>
  );

  const baseClasses =
    'bg-white rounded-2xl border border-slate-200 p-4 transition-all duration-200';

  if (to && !loading) {
    return (
      <button
        type="button"
        onClick={() => navigate(to)}
        className={`${baseClasses} text-left hover:border-primary/30 hover:shadow-sm cursor-pointer w-full`}
      >
        {content}
      </button>
    );
  }

  return <div className={baseClasses}>{content}</div>;
}
